import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Container, Row, Col, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserProfile, updateUserDetails } from '../../services/userService';
import { createShop, updateShop } from '../../services/shopService';
import { db } from '../../firebase/config';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import Toast from '../../components/Toast';
import { uploadImage, deleteImage } from '../../services/cloudinaryService';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const MAX_IMAGE_DIMENSION = 2048; // 2048px

const CATEGORIES = [
  'Abbigliamento',
  'Accessori',
  'Cosmesi',
  'Casalinghi',
  'Elettronica',
  'Food',
  'Altro'
];

const ShopSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [shopName, setShopName] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [shopImage, setShopImage] = useState(null);
  const [shopImagePreview, setShopImagePreview] = useState('');
  const [phone, setPhone] = useState('');
  const [wechatId, setWechatId] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [openingHours, setOpeningHours] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [uploading, setUploading] = useState(false);
  const [imagePublicId, setImagePublicId] = useState(null);

  useEffect(() => {
    const loadShopData = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        const profile = await getUserProfile(currentUser.uid);
        if (profile) {
          setShopName(profile.shopName || '');
          setSubtitle(profile.shopSubtitle || '');
          setDescription(profile.shopDescription || '');
          setCategory(profile.shopCategory || '');
          setShopImagePreview(profile.logoUrl || '');
          setPhone(profile.phone || '');
          setWechatId(profile.wechatId || '');
          setAddress(profile.address || '');
          setCity(profile.city || '');
          setPostalCode(profile.postalCode || '');
          setOpeningHours(profile.openingHours || '');
        }

        // Carica anche i dati del negozio per ottenere l'immagine
        const shopRef = doc(db, 'shops', currentUser.uid);
        const shopDoc = await getDoc(shopRef);
        if (shopDoc.exists()) {
          const shopData = shopDoc.data();
          if (shopData.logoUrl) {
            setShopImagePreview(shopData.logoUrl);
          }
        }
      } catch (err) {
        console.error('Errore nel caricamento dei dati del negozio:', err);
        setError('Errore nel caricamento dei dati del negozio');
        setToastMessage('Errore nel caricamento dei dati del negozio');
        setToastVariant('danger');
        setShowToast(true);
      } finally {
        setLoading(false);
      }
    };

    loadShopData();
  }, [currentUser, navigate]);

  const validateImage = (file) => {
    return new Promise((resolve, reject) => {
      if (file.size > MAX_FILE_SIZE) {
        reject('L\'immagine non deve superare i 2MB');
        return;
      }

      const img = new Image();
      img.onload = () => {
        if (img.width > MAX_IMAGE_DIMENSION || img.height > MAX_IMAGE_DIMENSION) {
          reject(`L'immagine non deve superare i ${MAX_IMAGE_DIMENSION}x${MAX_IMAGE_DIMENSION} pixel`);
        } else {
          resolve();
        }
      };
      img.onerror = () => reject('Errore nel caricamento dell\'immagine');
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        await validateImage(file);
        setUploading(true);
        
        // Carica l'immagine su Cloudinary
        const result = await uploadImage(file);
        
        // Aggiorna lo stato con l'URL dell'immagine e il public ID
        setShopImagePreview(result.url);
        setImagePublicId(result.publicId);
        
        // Se c'era un'immagine precedente, eliminiamola da Cloudinary
        if (imagePublicId) {
          await deleteImage(imagePublicId);
        }
      } catch (error) {
        setToastMessage(error);
        setToastVariant('danger');
        setShowToast(true);
        e.target.value = '';
      } finally {
        setUploading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // 1. Aggiorna i dettagli dell'utente
      await updateUserDetails(currentUser.uid, {
        shopName,
        shopSubtitle: subtitle,
        shopDescription: description,
        shopCategory: category,
        logoUrl: shopImagePreview,
        phone,
        wechatId,
        address,
        city,
        postalCode,
        openingHours,
        updatedAt: new Date().toISOString()
      });

      // 2. Crea/Aggiorna il documento del negozio
      const shopData = {
        ownerId: currentUser.uid,
        name: shopName,
        subtitle: subtitle,
        description: description,
        category: category,
        logoUrl: shopImagePreview,
        contact: {
          phone,
          wechatId
        },
        address: {
          street: address,
          city,
          postalCode
        },
        openingHours,
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Usa l'ID dell'utente come ID del negozio
      const shopRef = doc(db, 'shops', currentUser.uid);
      
      // Verifica se il documento esiste
      const shopDoc = await getDoc(shopRef);
      
      if (shopDoc.exists()) {
        // Se esiste, aggiorna solo i campi modificati
        await updateDoc(shopRef, {
          ...shopData,
          updatedAt: new Date().toISOString()
        });
      } else {
        // Se non esiste, crea un nuovo documento
        await setDoc(shopRef, shopData);
      }

      setToastMessage('Impostazioni negozio aggiornate con successo');
      setToastVariant('success');
      setShowToast(true);
      setTimeout(() => navigate('/seller'), 1000);
    } catch (err) {
      console.error("Errore nel salvataggio delle impostazioni:", err);
      setError('Errore durante il salvataggio');
      setToastMessage('Errore durante il salvataggio');
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  return (
    <Container className="py-4 d-flex justify-content-center">
      <Toast 
        show={showToast} 
        onClose={() => setShowToast(false)} 
        message={toastMessage}
        variant={toastVariant}
      />
      <Card className="p-4 shadow-sm border-0 rounded-4" style={{ maxWidth: 800, width: '100%' }}>
        <Card.Body>
          <h4 className="fw-bold mb-4">Impostazioni Negozio</h4>
          <Form onSubmit={handleSubmit}>
            {/* Preview immagine negozio */}
            <div className="d-flex flex-column align-items-center mb-4">
              <div 
                style={{ 
                  width: 150, 
                  height: 150, 
                  borderRadius: '12px', 
                  overflow: 'hidden', 
                  background: '#f2f2f2',
                  marginBottom: 8 
                }}
              >
                {shopImagePreview ? (
                  <img 
                    src={shopImagePreview} 
                    alt="shop preview" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                  />
                ) : (
                  <div className="w-100 h-100 d-flex align-items-center justify-content-center">
                    <i className="bi bi-shop" style={{ fontSize: 80, color: '#e0e0e0' }}></i>
                  </div>
                )}
              </div>
              <Form.Group controlId="shopImageFile" className="w-100">
                <Form.Label className="small">Immagine Negozio</Form.Label>
                <Form.Control 
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageChange}
                  disabled={uploading}
                />
                <Form.Text className="text-muted">
                  {uploading ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : null}
                  Dimensioni massime: 2MB. Risoluzione massima: {MAX_IMAGE_DIMENSION}x{MAX_IMAGE_DIMENSION} pixel
                </Form.Text>
              </Form.Group>
            </div>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="shopName">
                  <Form.Label>Nome Negozio</Form.Label>
                  <Form.Control
                    type="text"
                    value={shopName}
                    onChange={e => setShopName(e.target.value)}
                    required
                    placeholder="Inserisci il nome del tuo negozio"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="category">
                  <Form.Label>Categoria</Form.Label>
                  <Form.Select
                    value={category}
                    onChange={e => setCategory(e.target.value)}
                    required
                  >
                    <option value="">Seleziona una categoria</option>
                    {CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3" controlId="subtitle">
              <Form.Label>Sottotitolo</Form.Label>
              <Form.Control
                type="text"
                value={subtitle}
                onChange={e => setSubtitle(e.target.value)}
                placeholder="Inserisci un sottotitolo per il tuo negozio"
              />
              <Form.Text className="text-muted">
                Un breve sottotitolo che descrive il tuo negozio
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="description">
              <Form.Label>Descrizione</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Inserisci una breve descrizione del tuo negozio"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="phone">
                  <Form.Label>Numero Telefonico</Form.Label>
                  <Form.Control
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="Inserisci il numero di telefono"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="wechatId">
                  <Form.Label>ID WeChat</Form.Label>
                  <Form.Control
                    type="text"
                    value={wechatId}
                    onChange={e => setWechatId(e.target.value)}
                    placeholder="Inserisci il tuo ID WeChat"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3" controlId="address">
              <Form.Label>Indirizzo</Form.Label>
              <Form.Control
                type="text"
                value={address}
                onChange={e => setAddress(e.target.value)}
                placeholder="Inserisci l'indirizzo del negozio"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="city">
                  <Form.Label>Città</Form.Label>
                  <Form.Control
                    type="text"
                    value={city}
                    onChange={e => setCity(e.target.value)}
                    placeholder="Inserisci la città"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="postalCode">
                  <Form.Label>CAP</Form.Label>
                  <Form.Control
                    type="text"
                    value={postalCode}
                    onChange={e => setPostalCode(e.target.value)}
                    placeholder="Inserisci il CAP"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4" controlId="openingHours">
              <Form.Label>Orari di Apertura</Form.Label>
              <Form.Control
                type="text"
                value={openingHours}
                onChange={e => setOpeningHours(e.target.value)}
                placeholder="Es: Lun-Ven 9:00-18:00, Sab 9:00-12:00"
              />
              <Form.Text className="text-muted">
                Inserisci gli orari di apertura del tuo negozio
              </Form.Text>
            </Form.Group>

            {error && <div className="text-danger mb-3">{error}</div>}
            
            <Row>
              <Col xs={6}>
                <Button 
                  variant="outline-secondary" 
                  className="w-100 rounded-pill" 
                  onClick={() => navigate('/seller')} 
                  disabled={saving}
                >
                  {t('common.cancel')}
                </Button>
              </Col>
              <Col xs={6}>
                <Button 
                  type="submit" 
                  variant="primary" 
                  className="w-100 rounded-pill" 
                  disabled={saving}
                >
                  {saving ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    t('common.save')
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ShopSettings; 